import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
const suggestionPopoverKey = new PluginKey('suggestionPopover');
export const SuggestionPopoverExtension = Extension.create({
    name: 'suggestionPopover',
    addOptions() {
        return {
            Component: () => null,
            suggestion: null,
            onPortalsChange: undefined,
        };
    },
    addProseMirrorPlugins() {
        const extension = this;
        return [
            new Plugin({
                key: suggestionPopoverKey,
                state: {
                    init() {
                        return {
                            decorations: DecorationSet.empty,
                            portals: new Map(),
                        };
                    },
                    apply(tr, value, oldState, newState) {
                        const { Component, suggestion, onPortalsChange } = extension.options;
                        const { portals } = value;
                        if (!Component || !suggestion) {
                            // Clean up all portals
                            portals.forEach(({ container }) => {
                                container.remove();
                            });
                            portals.clear();
                            if (onPortalsChange) {
                                onPortalsChange(new Map());
                            }
                            return {
                                decorations: DecorationSet.empty,
                                portals: new Map(),
                            };
                        }
                        // Add validation to ensure document is in a valid state
                        if (!newState.doc || newState.doc.content.size === 0) {
                            return {
                                decorations: DecorationSet.empty,
                                portals: new Map(portals),
                            };
                        }
                        const decorations = [];
                        const foundSuggestions = new Map();
                        // Find all suggestion marks in the document
                        try {
                            newState.doc.descendants((node, pos) => {
                                if (node.isText && node.marks.length > 0) {
                                    node.marks.forEach((mark) => {
                                        if ((mark.type.name === 'suggestionAdd' ||
                                            mark.type.name === 'suggestionDelete') &&
                                            mark.attrs.suggestionId) {
                                            const existing = foundSuggestions.get(mark.attrs.suggestionId);
                                            if (!existing) {
                                                // Validate position is within document bounds
                                                if (pos >= 0 &&
                                                    pos + node.nodeSize <=
                                                        newState.doc.content.size) {
                                                    foundSuggestions.set(mark.attrs.suggestionId, {
                                                        from: pos,
                                                        to: pos + node.nodeSize,
                                                    });
                                                }
                                            }
                                            else {
                                                // Extend the range if we find more text with the same suggestion
                                                const newTo = pos + node.nodeSize;
                                                if (newTo <= newState.doc.content.size) {
                                                    existing.to = Math.max(existing.to, newTo);
                                                }
                                            }
                                        }
                                    });
                                }
                            });
                        }
                        catch (error) {
                            console.warn('Error while scanning document for suggestions:', error);
                            return {
                                decorations: DecorationSet.empty,
                                portals: new Map(portals),
                            };
                        }
                        // Create widget decorations for each suggestion
                        foundSuggestions.forEach((range, suggestionId) => {
                            const operation = suggestion.operations.find((op) => op.id === suggestionId);
                            if (!operation) {
                                return;
                            }
                            // Validate range is still within document bounds
                            if (range.from < 0 ||
                                range.to > newState.doc.content.size ||
                                range.from >= range.to) {
                                console.warn(`Invalid suggestion range for ${suggestionId}:`, range, 'Document size:', newState.doc.content.size);
                                return;
                            }
                            // Create handlers for this specific suggestion
                            const handleAccept = () => {
                                extension.editor
                                    .chain()
                                    .focus()
                                    .command(({ tr, state }) => {
                                    state.doc.descendants((node, nodePos) => {
                                        node.marks.forEach((m) => {
                                            if ((m.type.name === 'suggestionAdd' ||
                                                m.type.name ===
                                                    'suggestionDelete') &&
                                                m.attrs.suggestionId === suggestionId) {
                                                tr.removeMark(nodePos, nodePos + node.nodeSize, m.type);
                                            }
                                        });
                                    });
                                    return true;
                                })
                                    .run();
                            };
                            const handleReject = () => {
                                extension.editor
                                    .chain()
                                    .focus()
                                    .command(({ tr, state }) => {
                                    if (operation.kind === 'insert' ||
                                        operation.kind === 'update') {
                                        // First, collect all positions with suggestion add marks
                                        const markedRanges = [];
                                        const blocksToDelete = new Set();
                                        state.doc.descendants((node, nodePos) => {
                                            if (node.isText) {
                                                node.marks.forEach((mark) => {
                                                    if (mark.type.name ===
                                                        'suggestionAdd' &&
                                                        mark.attrs.suggestionId ===
                                                            suggestionId) {
                                                        markedRanges.push({
                                                            from: nodePos,
                                                            to: nodePos +
                                                                node.nodeSize,
                                                        });
                                                    }
                                                });
                                            }
                                        });
                                        // Now check if any blocks should be entirely deleted
                                        state.doc.descendants((node, nodePos) => {
                                            if (node.isBlock &&
                                                node.content.size > 0) {
                                                let blockStart = nodePos + 1; // Start of block content
                                                let blockEnd = nodePos + node.nodeSize - 1; // End of block content
                                                // Check if all content in this block is marked for deletion
                                                let allContentMarked = true;
                                                let hasMarkedContent = false;
                                                node.content.forEach((child, offset) => {
                                                    const childPos = blockStart + offset;
                                                    if (child.isText &&
                                                        child.text &&
                                                        child.text.trim() !== '') {
                                                        const isMarked = markedRanges.some((range) => range.from <=
                                                            childPos &&
                                                            childPos <
                                                                range.to);
                                                        if (isMarked) {
                                                            hasMarkedContent =
                                                                true;
                                                        }
                                                        else {
                                                            allContentMarked =
                                                                false;
                                                        }
                                                    }
                                                });
                                                // If all non-empty content is marked, delete the entire block
                                                if (hasMarkedContent &&
                                                    allContentMarked) {
                                                    blocksToDelete.add(nodePos);
                                                }
                                            }
                                        });
                                        // Delete entire blocks first (in reverse order)
                                        const sortedBlockPositions = Array.from(blocksToDelete).sort((a, b) => b - a);
                                        sortedBlockPositions.forEach((pos) => {
                                            const node = state.doc.nodeAt(pos);
                                            if (node) {
                                                tr.delete(pos, pos + node.nodeSize);
                                            }
                                        });
                                        // Then delete remaining marked text that wasn't in deleted blocks
                                        if (sortedBlockPositions.length === 0) {
                                            // Only delete text if we didn't delete any blocks
                                            markedRanges
                                                .sort((a, b) => b.from - a.from)
                                                .forEach(({ from, to }) => {
                                                tr.delete(from, to);
                                            });
                                        }
                                    }
                                    if (operation.kind === 'update' ||
                                        operation.kind === 'delete') {
                                        // Remove delete marks
                                        state.doc.descendants((node, nodePos) => {
                                            node.marks.forEach((m) => {
                                                if (m.type.name ===
                                                    'suggestionDelete' &&
                                                    m.attrs.suggestionId ===
                                                        suggestionId) {
                                                    tr.removeMark(nodePos, nodePos + node.nodeSize, m.type);
                                                }
                                            });
                                        });
                                    }
                                    return true;
                                })
                                    .run();
                            };
                            // Get or create container
                            let portalInfo = portals.get(suggestionId);
                            if (!portalInfo) {
                                const container = document.createElement('div');
                                container.style.position = 'absolute';
                                container.style.zIndex = '1000';
                                container.className = `suggestion-popover-${suggestionId}`;
                                portalInfo = {
                                    container,
                                    props: {
                                        suggestion,
                                        position: { top: 0, left: 0 },
                                        onAccept: handleAccept,
                                        onReject: handleReject,
                                        isVisible: true,
                                    },
                                };
                                portals.set(suggestionId, portalInfo);
                            }
                            else {
                                // Update the props with new handlers
                                portalInfo.props = {
                                    ...portalInfo.props,
                                    onAccept: handleAccept,
                                    onReject: handleReject,
                                };
                            }
                            // Create a widget decoration at the start of the suggestion
                            try {
                                const widget = Decoration.widget(range.from, // Position at the start to get vertical position
                                (view) => {
                                    const wrapper = document.createElement('div');
                                    wrapper.style.position = 'relative';
                                    wrapper.style.display = 'inline-block';
                                    wrapper.style.width = '0';
                                    wrapper.style.height = '0';
                                    // Position the popover immediately when widget is created
                                    const positionPopover = () => {
                                        try {
                                            // Validate position is still within document bounds
                                            if (range.from >= 0 &&
                                                range.from <=
                                                    view.state.doc.content.size) {
                                                const coords = view.coordsAtPos(range.from);
                                                const editorElement = view.dom.parentElement;
                                                if (editorElement && coords) {
                                                    const editorRect = editorElement.getBoundingClientRect();
                                                    const relativeTop = coords.top - editorRect.top;
                                                    // Position the popover fixed to the right of the editor
                                                    portalInfo.container.style.position =
                                                        'absolute';
                                                    portalInfo.container.style.top = `${relativeTop + 20}px`;
                                                    portalInfo.container.style.right =
                                                        '0';
                                                    portalInfo.container.style.left =
                                                        'auto';
                                                    portalInfo.container.style.transform =
                                                        'translateX(100%)';
                                                    portalInfo.container.style.visibility =
                                                        'visible';
                                                }
                                            }
                                        }
                                        catch (error) {
                                            console.warn('Failed to position suggestion popover:', error);
                                        }
                                    };
                                    // Set up the container and append it
                                    const setupContainer = () => {
                                        const editorElement = view.dom.parentElement;
                                        if (editorElement) {
                                            if (editorElement.style.position !==
                                                'relative') {
                                                editorElement.style.position =
                                                    'relative';
                                            }
                                            if (portalInfo.container
                                                .parentElement !== editorElement) {
                                                editorElement.appendChild(portalInfo.container);
                                            }
                                            // Position after container is in DOM
                                            positionPopover();
                                        }
                                    };
                                    // Initially hide until positioned
                                    portalInfo.container.style.visibility = 'hidden';
                                    // Use requestAnimationFrame to ensure DOM is ready
                                    requestAnimationFrame(setupContainer);
                                    return wrapper;
                                }, {
                                    key: suggestionId,
                                    side: -1, // Place before the content
                                });
                                decorations.push(widget);
                            }
                            catch (error) {
                                console.warn(`Failed to create decoration for suggestion ${suggestionId}:`, error);
                            }
                        });
                        // Clean up containers for suggestions that no longer exist
                        portals.forEach((portalInfo, id) => {
                            if (!foundSuggestions.has(id)) {
                                portalInfo.container.remove();
                                portals.delete(id);
                            }
                        });
                        // Notify parent component about portal changes
                        if (onPortalsChange) {
                            onPortalsChange(new Map(portals));
                        }
                        try {
                            return {
                                decorations: DecorationSet.create(newState.doc, decorations),
                                portals,
                            };
                        }
                        catch (error) {
                            console.warn('Failed to create decoration set:', error);
                            return {
                                decorations: DecorationSet.empty,
                                portals,
                            };
                        }
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state)?.decorations || DecorationSet.empty;
                    },
                },
                view() {
                    let resizeObserver = null;
                    let editorElement = null;
                    let updatePositions = null;
                    return {
                        update(view, prevState) {
                            // Only update positions if the document has actually changed
                            if (!prevState || !view.state.doc.eq(prevState.doc)) {
                                updatePositions = () => {
                                    const pluginState = suggestionPopoverKey.getState(view.state);
                                    if (!pluginState?.portals)
                                        return;
                                    pluginState.portals.forEach((portalInfo, suggestionId) => {
                                        // Find the suggestion range in current document
                                        let foundPos = null;
                                        view.state.doc.descendants((node, pos) => {
                                            if (node.isText &&
                                                node.marks.length > 0 &&
                                                foundPos === null) {
                                                node.marks.forEach((mark) => {
                                                    if ((mark.type.name ===
                                                        'suggestionAdd' ||
                                                        mark.type.name ===
                                                            'suggestionDelete') &&
                                                        mark.attrs.suggestionId ===
                                                            suggestionId &&
                                                        foundPos === null) {
                                                        foundPos = pos;
                                                    }
                                                });
                                            }
                                        });
                                        if (foundPos !== null) {
                                            try {
                                                // Validate position is still within document bounds
                                                if (foundPos >= 0 &&
                                                    foundPos <=
                                                        view.state.doc.content.size) {
                                                    const coords = view.coordsAtPos(foundPos);
                                                    const editorElement = view.dom.parentElement;
                                                    if (editorElement && coords) {
                                                        const editorRect = editorElement.getBoundingClientRect();
                                                        const relativeTop = coords.top -
                                                            editorRect.top;
                                                        portalInfo.container.style.top = `${relativeTop + 20}px`;
                                                    }
                                                }
                                            }
                                            catch (error) {
                                                console.warn('Failed to update suggestion popover position:', error);
                                            }
                                        }
                                    });
                                };
                                // Update positions immediately if we have a function
                                if (updatePositions) {
                                    updatePositions();
                                }
                            }
                            // Set up resize observer if not already set
                            if (!resizeObserver && view.dom.parentElement) {
                                editorElement = view.dom.parentElement;
                                resizeObserver = new ResizeObserver(() => {
                                    if (updatePositions)
                                        updatePositions();
                                });
                                resizeObserver.observe(editorElement);
                                // Also observe window resize
                                if (updatePositions) {
                                    window.addEventListener('resize', updatePositions);
                                }
                            }
                        },
                        destroy() {
                            // Clean up resize observer
                            if (resizeObserver && editorElement) {
                                resizeObserver.disconnect();
                                resizeObserver = null;
                            }
                            // Remove window resize listener
                            if (updatePositions) {
                                window.removeEventListener('resize', updatePositions);
                                updatePositions = null;
                            }
                            // Clean up all containers on destroy
                            const pluginState = suggestionPopoverKey.getState(extension.editor.state);
                            if (pluginState?.portals) {
                                pluginState.portals.forEach(({ container }) => {
                                    container.remove();
                                });
                            }
                        },
                    };
                },
            }),
        ];
    },
});
//# sourceMappingURL=suggestionPopoverExtension.js.map